import { styled, alpha, type Theme } from '@mui/material/styles';
import { Box, Autocomplete, IconButton, Typography, Chip } from '@mui/material';
import type { Product } from './ProductForm.hooks';
import { ACCENT_COLOR } from '../../../../styles/theme/accentFieldSx';

export const ProductFormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 2),
  borderRadius: '12px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  '&:focus-within': {
    borderColor: ACCENT_COLOR,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 0 0 3px ${alpha(ACCENT_COLOR, theme.palette.mode === 'dark' ? 0.35 : 0.2)}`,
  },
}));

export const ProductFormHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.25),
}));

export const ProductRowNumber = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
}));

export const PieceRangeLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  lineHeight: 1.2,
}));

export const RemoveButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
  borderRadius: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '40px',
  height: '40px',
  marginTop: '18px', // Centrado más arriba, alineado con el centro del input
  flexShrink: 0,
  alignSelf: 'flex-start',
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '15',
    border: 'none',
  },
  '&:active': {
    transform: 'scale(0.92)',
  },
  '&::before': {
    display: 'none',
  },
  '&::after': {
    display: 'none',
  },
}));

export const FormFieldsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto', // Producto flexible, Cantidad con botón
  gap: theme.spacing(1.5),
  alignItems: 'start',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const QuantityContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  width: '70px',
  position: 'relative',
  '&::before': {
    display: 'none',
  },
  '&::after': {
    display: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

// El color de borde/foco de este campo lo da el tema global (verde-teal de marca), igual que
// Proveedor y el buscador de producto — ver comentario en StyledAutocomplete más abajo.
const inputSurface = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? {
        backgroundColor: alpha(theme.palette.common.white, 0.06),
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.1),
        },
        '&.Mui-focused': {
          backgroundColor: alpha(theme.palette.common.white, 0.12),
        },
      }
    : {
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
          backgroundColor: theme.palette.background.paper,
        },
        '&.Mui-focused': {
          backgroundColor: theme.palette.background.paper,
        },
      };

const identityChipLabel = {
  paddingLeft: 6,
  paddingRight: 6,
  fontSize: '0.75rem',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const ProductIdentityChipsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

export const SelectedProductIdentity = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  pointerEvents: 'none',
}));

export const SelectedProductName = styled(Box)(() => ({
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
}));

export const ProductCodeChip = styled(Chip)(({ theme }) => ({
  height: 22,
  maxWidth: '100%',
  pointerEvents: 'none',
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.dark,
  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
  '& .MuiChip-label': identityChipLabel,
}));

export const SupplierCodeChip = styled(Chip)(({ theme }) => ({
  height: 22,
  maxWidth: '100%',
  pointerEvents: 'none',
  borderColor: theme.palette.warning.main,
  color: theme.palette.warning.dark,
  backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
  '& .MuiChip-label': identityChipLabel,
}));

// El color de borde/foco de este campo (idle → hover → focus) ya lo da el tema global
// (MuiOutlinedInput/MuiInputLabel en src/styles/theme/index.ts + focus.ts) — es el mismo verde-teal
// de marca que usa cada campo de texto de toda la app. No se pisa acá a propósito.
export const StyledAutocomplete = styled(Autocomplete<Product>)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    // Sin fontSize propio: hereda el tamaño default de MUI, igual que Proveedor y Cantidad
    // (antes tenía uno más grande que hacía que este campo se viera más alto que ellos).
    borderRadius: '10px',
    columnGap: theme.spacing(0.75),
    ...inputSurface(theme),
  },
  '& .MuiInputLabel-root': {
    // Sin fontSize propio: mismo motivo — hereda el default, igual que las otras dos etiquetas.
    color: theme.palette.text.secondary,
    fontWeight: 400,
    transition: theme.transitions.create(['color', 'transform'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  '& .MuiAutocomplete-input': {
    // Sin fontSize/padding propio: hereda el default de MUI, igual que Proveedor y
    // Cantidad (antes tenía valores a mano que lo hacían verse distinto a los otros dos campos).
    minWidth: '4ch',
  },
  '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
    paddingRight: '56px',
  },
  '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
    paddingRight: '72px',
  },
}));

export const ErrorMessage = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 400,
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  padding: 0,
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const SuccessMessage = styled(Typography)(({ theme }) => ({
  display: 'none', // OCULTO COMPLETAMENTE
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  borderRadius: '10px',
  border: `1px dashed ${theme.palette.divider}`,
  gridColumn: '1 / -1',
  opacity: 0.7,
  backgroundColor: theme.palette.background.paper,
}));
